// src/oura/components/RefreshControl.tsx
//
// The refresh control, its confirm dialog and its in-card progress (#3904,
// epic #3903) — one shape for every card that can re-generate its own data.
//
// EVERY CONTROL ASKS FIRST, including the free ones. The dialog is not only
// about money: it is the moment a staffer is told WHICH CARDS ARE ABOUT TO
// CHANGE, and a control that re-generates data silently is one they cannot
// predict. What differs between a free recompute and a paid re-measure is the
// dialog's content, not whether it appears.

import { PlugIcon, RefreshCwIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { PillButton, PillLink, TYPE } from "@oura/ui";
import { ouraHref } from "../nav";
import { cn } from "../lib/cn";
import type { RefreshJob } from "../hooks/useRefreshJob";
import { refreshOutcomeLabel, type RefreshJobSpec } from "../refreshJobs";
import type { AeoRerunKind } from "@service/aeo";

/**
 * The button. Disabled while the job runs, and labelled so the card never
 * looks idle over work that is still going.
 */
export function RefreshButton({
    job,
    title,
    label = "Refresh",
    runningLabel = "Refreshing…",
}: {
    // `unknown` because the button does not read the result — only the job's
    // own state. Any RefreshJob fits.
    job: RefreshJob<unknown>;
    title?: string;
    label?: string;
    runningLabel?: string;
}) {
    return (
        <PillButton size="sm" icon={RefreshCwIcon} busy={job.running} onClick={job.ask} title={title}>
            {job.running ? runningLabel : label}
        </PillButton>
    );
}

/**
 * The confirm dialog.
 *
 * `cards` is not decoration. A refresh is a job over a SOURCE, and one source
 * feeds several cards — a dialog that names only the card that was clicked is
 * lying by omission about what is going to move.
 */
export function RefreshConfirmDialog({
    job,
    title,
    cards,
    children,
    confirmLabel = "Refresh",
    busy = false,
}: {
    // Only what the dialog actually drives, so any refresh hook fits — the
    // stage re-run and the Google AI read both have this much and differ
    // everywhere else.
    job: Pick<RefreshJob<unknown>, "confirmOpen" | "cancel" | "run">;
    title: string;
    /** Every card this job changes, not just the one that was clicked. */
    cards?: string[];
    /** What it does, what it costs, and what it does NOT touch. */
    children: ReactNode;
    confirmLabel?: string;
    /** The request is in flight. The dialog stays open and its button works,
     *  because closing on the press and reporting the outcome somewhere else
     *  is what makes a press look like nothing happened. */
    busy?: boolean;
}) {
    if (!job.confirmOpen) return null;
    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={job.cancel} />
            <div className="absolute left-1/2 top-1/2 w-full max-w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
                <h2 className="my-0 text-lg font-semibold leading-snug text-gray-900">{title}</h2>
                <div className="mt-2 space-y-2 text-sm leading-relaxed text-gray-600">{children}</div>
                {cards && cards.length > 0 && (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className={cn(TYPE.lab, "my-0")}>
                            {cards.length === 1 ? "This card changes" : "These cards change"}
                        </p>
                        <p className="my-0 mt-1 text-xs leading-relaxed text-gray-700">{cards.join(" · ")}</p>
                    </div>
                )}
                <div className="mt-5 flex justify-end gap-2">
                    {/* Cancel fires nothing. Nothing is started, nothing is
                        stored, and every card is exactly as it was. */}
                    <PillButton size="sm" onClick={job.cancel} disabled={busy}>Cancel</PillButton>
                    <PillButton size="sm" variant="primary" icon={RefreshCwIcon} busy={busy} onClick={() => void job.run()}>
                        {busy ? "Starting…" : confirmLabel}
                    </PillButton>
                </div>
            </div>
        </div>
    );
}

/**
 * The in-card "still going" strip, plus whatever the job has to say.
 *
 * A bar with no percentage on purpose: the backend reports no progress, and a
 * bar that crept to 90% would be a guess. It says "still going" and nothing
 * more — the same treatment a knowledge source gets while it is being read.
 */
export function RefreshProgress({
    job,
    runningText,
}: {
    job: RefreshJob<unknown> & { kind?: AeoRerunKind | null; cost?: number | null; needsConnector?: boolean };
    /** What is happening, in a few words. Absent while nothing is running. */
    runningText?: ReactNode;
}) {
    const { businessId } = useParams<{ businessId?: string }>();
    // A run that failed measured nothing: "Measured again" under a failure
    // note told staff the opposite of what the note said.
    const failed = job.note?.tone === "warn";
    const outcome = failed ? null : refreshOutcomeLabel(job.kind, job.cost);
    if (!job.running && !job.note && outcome === null) return null;
    return (
        <>
            {job.running && (
                <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
                    <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-500" />
                </div>
            )}
            {job.running && runningText && (
                <p className="my-0 text-xs text-gray-500">{runningText}</p>
            )}
            {job.note && (
                <p className={cn(
                    "my-0 text-xs leading-relaxed",
                    job.note.tone === "warn" ? "text-amber-700" : "text-gray-500",
                )}>
                    {job.note.text}
                </p>
            )}
            {job.note && job.needsConnector && businessId && (
                <div className="pt-1">
                    <PillLink size="sm" icon={PlugIcon} to={ouraHref("connectors", { role: "oura-staff", businessId })}>
                        Open Connectors
                    </PillLink>
                </div>
            )}
            {/* What this run actually did, from the KIND the backend reported.
                Not from the figure: it is 0.0 for every stage, so reading
                "free" off it would call a crawl of a client's whole site
                free. */}
            {outcome !== null && (
                <p className="my-0 text-xs text-gray-400">{outcome}</p>
            )}
        </>
    );
}

/**
 * A card's refresh, from its spec: the button and its dialog in one place
 * (epic #3903).
 *
 * Screens render this rather than assembling the pieces, so the dialog can
 * never be wired up without the card list — which is the one thing that makes a
 * source-level job honest about what it moves.
 */
export function CardRefresh({
    job,
    spec,
    label,
}: {
    job: RefreshJob<never> & { kind?: AeoRerunKind | null; cost?: number | null };
    spec: RefreshJobSpec;
    /** Override for a card that needs a narrower word than "Refresh". */
    label?: string;
}) {
    const free = spec.expected === "recompute";
    return (
        <>
            <RefreshButton
                job={job}
                label={label ?? (free ? "Recompute" : "Refresh")}
                runningLabel={free ? "Recomputing…" : "Refreshing…"}
                title={spec.what}
            />
            <RefreshConfirmDialog
                job={job}
                title={spec.title}
                cards={spec.cards}
                confirmLabel={free ? "Recompute" : "Refresh"}
            >
                <p className="my-0">{spec.what}</p>
                <p className="my-0">{spec.keeps}</p>
                {/* What it costs, in the only terms we honestly have before
                    asking. There is no dry-run: the backend states the real
                    figure when the job starts, and `RefreshProgress` reports
                    it then. A number guessed here would be the one figure on
                    the screen nobody measured. */}
                <p className="my-0 text-xs text-gray-500">
                    {free
                        ? "Nothing is measured again, so this costs nothing to run."
                        : "This one measures again: it spends API quota and bandwidth rather than a billed service, so there is no dollar figure to quote."}
                </p>
            </RefreshConfirmDialog>
        </>
    );
}
